// Per-route SEO metadata for the static marketing + funnel pages.
//
// Page-level JSON-LD (FAQPage, Product/Offer, Service) is attached here too so
// it lands in the prerendered HTML. Resource/tool pages (/resources/*, /tools/*)
// manage their own <Seo> and are intentionally NOT listed here.

import { faqData } from "../content/faq";
import { FAQS as PHOTO_VIDEO_FAQS } from "../content/photoVideo";
import { blocksToPlainText } from "../content/richText";
import {
  ACTION_COSTS,
  FREE_MONTHLY_CREDITS,
  PACKS,
  PACK_EXPIRY_MONTHS,
  PLANS,
  PLAN_LABELS,
  formatDollars,
} from "./credits";
import { isPrivateRoute, normalizePath } from "./routes";

const SITE = "https://listino.ai";
/** @typedef {{title: string, path: string, description?: string, jsonLd?: object | object[], noindex?: boolean}} SeoMetadata */

// Prefixes whose pages render their own <Seo>; the central RouteSeo skips them.
const SELF_MANAGED_PREFIXES = ["/resources", "/tools"];

// ---------------------------------------------------------------------------
// Page-level structured data (JSON-LD). Emitted into the (prerendered) HTML so
// both Google and non-JS AI crawlers can extract it.
// ---------------------------------------------------------------------------

const ORG_REF = { "@type": "Organization", name: "Listino", url: `${SITE}/` };

const faqPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqData.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: blocksToPlainText(f.answer) },
  })),
};

// The offers are the things sold for dollars — the plans and the
// credit packs. Credits are not a currency, so per-action credit prices never
// appear as an Offer; they live in the Product description as plain text.
const num = (/** @type {number} */ n) => n.toLocaleString("en-US");
/** @type {Record<string, string>} */
const INTERVAL_WORD = { month: "a month", year: "a year" };
const creditsProductOffers = [
  {
    "@type": "Offer",
    name: `Free plan — ${num(FREE_MONTHLY_CREDITS)} credits a month`,
    price: 0,
    priceCurrency: "USD",
    url: `${SITE}/pricing`,
    availability: "https://schema.org/InStock",
  },
  ...Object.values(PLANS).map((plan) => ({
    "@type": "Offer",
    name: `${PLAN_LABELS[plan.product]} plan — ${num(plan.credits)} credits ${INTERVAL_WORD[plan.interval]}`,
    price: plan.amountCents / 100,
    priceCurrency: "USD",
    url: `${SITE}/pricing`,
    availability: "https://schema.org/InStock",
  })),
  ...Object.values(PACKS).map((pack) => ({
    "@type": "Offer",
    name: `${num(pack.credits)}-credit pack (good for ${PACK_EXPIRY_MONTHS} months)`,
    price: pack.amountCents / 100,
    priceCurrency: "USD",
    url: `${SITE}/pricing`,
    availability: "https://schema.org/InStock",
  })),
];

const productJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Listino Listing Prep",
  description: `Same-day listing prep for real estate agents: Listing Strategy, Quick CMA, Listing Description, photo review, and a shareable web page, with a branded PDF, Seller Presentation, one-pager, social set, photo touch-ups, and video available on every plan. Priced in credits (a Listing Analysis is ${num(ACTION_COSTS.analysis)} credits); every account includes ${num(FREE_MONTHLY_CREDITS)} credits a month free.`,
  brand: { "@type": "Brand", name: "Listino" },
  offers: creditsProductOffers,
};

const listingReviewServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Listing Review",
  serviceType: "Real estate listing preparation",
  provider: ORG_REF,
  areaServed: "US",
  description:
    "A full prep for an active listing: market competitiveness, listing appeal and price position, an optimized listing-description rewrite, photo sequencing recommendations, AI photo touch-ups, and pricing grounded in comps (Quick CMA).",
  // There is no flat per-service price; the Product on /pricing carries the
  // real USD offers instead.
};

// Enriched Organization for the /about entity hub. `sameAs` is intentionally
// omitted until real third-party profiles exist (LinkedIn, Crunchbase, G2,
// Capterra, Product Hunt) — add them here once claimed (see the off-site
// grounding checklist) to strengthen the brand entity for AI engines.
const aboutOrgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Listino",
  url: `${SITE}/`,
  logo: `${SITE}/listino-logo.png`,
  slogan: "Extra hands for every listing.",
  description:
    "Listino is an AI listing assistant for real estate agents. It turns a Zillow URL or property details into same-day research, pricing guidance, listing copy, photo direction, and seller materials as an editable first draft agents verify, personalize, brand, and publish.",
  areaServed: "US",
  knowsAbout: [
    "Real estate listing optimization",
    "Comparative market analysis",
    "Listing descriptions",
    "Real estate listing photography",
    "Home staging",
    "Listing pricing strategy",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    email: "contact@listino.ai",
    contactType: "customer support",
  },
};

// /listing-photos-and-video. The FAQ entities come from the same module the
// page renders, so the structured data can never claim something the page
// doesn't say. Photos and video are add-ons to a prepared listing, so there is
// no flat Offer here — the credit prices are plain text in the description.
const photoVideoServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Listing Photo Touch-Ups and Property Video",
  serviceType: "Real estate listing photo editing and property video",
  provider: ORG_REF,
  areaServed: "US",
  description: `A photo review that names what could be improved in every listing photo — declutter, floor marks, yard debris, a patchy lawn, an overcast exterior, a dark room, overgrown landscaping — and edits only the photos the agent picks, at ${num(ACTION_COSTS.photo_touchup)} credits a photo. A short property video can be assembled from the listing's own photos at ${num(ACTION_COSTS.video)} credits, and waits for the agent's review before it can be published.`,
};

const photoVideoFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: PHOTO_VIDEO_FAQS.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const preListingServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Pre-Listing Prep",
  serviceType: "Pre-listing strategy and preparation",
  provider: ORG_REF,
  areaServed: "US",
  description:
    "A listing-ready strategy before going live: readiness assessment, a listing description written from scratch, photo strategy, AI photo touch-ups, and pricing grounded in comps (Quick CMA).",
};

/** @type {Record<string, SeoMetadata>} */
const indexable = {
  "/": {
    title: "Listino — Extra Hands for Every Listing",
    description:
      "Listino prepares same-day research, public-web comparable pricing guidance, listing copy, photo strategy, and AI touch-ups as an editable first draft agents verify, brand, and publish as a revocable listing package. Start free.",
    path: "/",
    jsonLd: [listingReviewServiceJsonLd, preListingServiceJsonLd],
  },
  "/get-started": {
    title: "Get Started — Review a Listing or Prep to List | Listino",
    description:
      "Import a Zillow listing or enter property details for a same-day editable listing prep with public-web candidate comps, pricing guidance, listing copy, photo strategy, AI touch-ups, branding, and publish-ready outputs.",
    path: "/get-started",
  },
  "/pricing": {
    title: `Pricing — Start Free, Plans from ${formatDollars(PLANS.agent_month.amountCents)}/mo | Listino`,
    description: `Listino pricing: every account includes ${num(FREE_MONTHLY_CREDITS)} free credits a month, no card required. Agent from ${formatDollars(PLANS.agent_month.amountCents)}/month, Pro from ${formatDollars(PLANS.pro_month.amountCents)}/month, or credit packs from ${formatDollars(PACKS.pack_1500.amountCents)}. A Listing Analysis is ${num(ACTION_COSTS.analysis)} credits; editing, branding, and sharing are always free.`,
    path: "/pricing",
    jsonLd: productJsonLd,
  },
  "/faq": {
    title: "Frequently Asked Questions | Listino",
    description:
      "How Listino works, what's included in your listing prep, turnaround time, pricing, and our refund policy — answers for agents and sellers.",
    path: "/faq",
    jsonLd: faqPageJsonLd,
  },
  "/about": {
    title: "About Listino — Listing Prep Built With Real Agents",
    description:
      "Listino is an AI listing assistant built for real estate agents. It prepares the research, analysis, and seller materials behind every listing — in minutes instead of hours. Learn who we are and how the analysis works.",
    path: "/about",
    jsonLd: aboutOrgJsonLd,
  },
  "/listing-photos-and-video": {
    title: "Listing Photo Editing & Property Video From Photos | Listino",
    description: `Real estate photo editing and property video, prepared with the listing. Listino's photo review names what to improve in every listing photo — declutter, brighten a dark room, green up the lawn — then edits only the photos you pick, ${num(ACTION_COSTS.photo_touchup)} credits a photo. A property video is built from those same photos for ${num(ACTION_COSTS.video)} credits and waits for your approval. ${num(FREE_MONTHLY_CREDITS)} free credits a month.`,
    path: "/listing-photos-and-video",
    jsonLd: [photoVideoServiceJsonLd, photoVideoFaqJsonLd],
  },
  "/privacy-policy": {
    title: "Privacy Policy | Listino",
    description: "How Listino collects, uses, and protects your information.",
    path: "/privacy-policy",
  },
  "/terms-and-conditions": {
    title: "Terms & Conditions | Listino",
    description: "The terms governing your use of Listino.",
    path: "/terms-and-conditions",
  },
};

// Transactional / authenticated routes: keep a real title but mark noindex so
// thin/dynamic pages don't compete in the index.
const NOINDEX_TITLE = "Listino";

// Private/transactional routes stay noindexed, but the browser tab should
// still say where the user is.
/** @type {Record<string, string>} */
const PRIVATE_TITLES = {
  "/verify": "Start with the address | Listino",
  "/submit": "Enter property details | Listino",
  "/login": "Sign in | Listino",
  "/listing-confirmation": "Confirm your listing | Listino",
  "/dashboard": "Your listings | Listino",
  "/account": "Your account | Listino",
  "/success": "You're all set | Listino",
};

/**
 * Resolve SEO metadata for a pathname.
 * Returns `null` for self-managed prefixes (the page renders its own <Seo>).
 * @param {string} pathname
 * @returns {SeoMetadata | null}
 */
export const getSeo = (pathname) => {
  const path = normalizePath(pathname);

  if (SELF_MANAGED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return null;
  }
  if (indexable[path]) return indexable[path];
  // Private/authenticated/transactional routes: real title but noindex.
  if (isPrivateRoute(path)) {
    return { title: PRIVATE_TITLES[path] || NOINDEX_TITLE, path, noindex: true };
  }
  // Unknown route → handled by the NotFound 404 page, which sets its own noindex.
  return null;
};

export { indexable as seoConfig };
